import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { handleFormErrors } from '../../form/form-errors';
import { useCreateUserApi } from '../api/use-create-user';
import { userRequestCreateSchema } from '../api/user-types';
// Sign-up Form Schema
export const signUpFormSchema = userRequestCreateSchema;
// Sign-up Form Hook
export const useSignUpForm = ({ onSuccess }) => {
    const defaultValues = useMemo(() => toFormData(), []);
    const form = useForm({
        resolver: zodResolver(signUpFormSchema),
        defaultValues,
    });
    // mutations
    const { mutate: createUser, isPending: isCreatePending } = useCreateUserApi({
        onSuccess,
        onError: (errors) => {
            handleFormErrors(form, errors);
        },
    });
    /**
     * Handles the form submission event, building the API payload and
     * invoking the create user mutation.
     *
     * @param values - Sanitized sign-up form values.
     */
    const submitForm = useCallback((values) => {
        const payload = toRequestData(values);
        createUser(payload);
    }, [createUser]);
    return {
        form,
        onSubmit: form.handleSubmit(submitForm),
        isSubmitting: isCreatePending,
    };
};
/**
 * Compute the initial values for the SignUpForm.
 *
 * @returns A fully populated SignUpForm with sensible defaults
 */
function toFormData() {
    return {
        email: '',
        password: '',
        timeZone: getDefaultTimeZone(),
    };
}
/**
 * Build the UserRequestCreate payload from form values.
 *
 * @param formValues - The values from the sign-up form
 * @returns A fully populated UserRequestCreate for the create operation
 */
function toRequestData(formValues) {
    return {
        email: formValues.email.trim().toLowerCase(),
        password: formValues.password,
        timeZone: formValues.timeZone,
    };
}
/**
 * Gets the user's default timezone, falling back to New York if detection fails.
 *
 * @returns The user's timezone string or 'America/New_York' as fallback
 */
function getDefaultTimeZone() {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York';
    }
    catch {
        return 'America/New_York';
    }
}
